import React, { useState, useEffect, useCallback } from 'react';
import {
    Flag,
    Filter,
    CheckCircle,
    Eye,
    ShieldAlert,
    MessageSquare,
    FileText
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getAssignedRegions, isRegionScopedAdmin, matchesAssignedRegions } from '../utils/regionAccess';
import reportService, { Report, ReportStats } from '../services/reportService';
import ExpandableText from './common/ui/ExpandableText';

const ReportManagement: React.FC = () => {
    const { adminProfile } = useAuth();
    const [reports, setReports] = useState<Report[]>([]);
    const [stats, setStats] = useState<ReportStats>({ pending: 0, resolved: 0, dismissed: 0, highSeverity: 0 });
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState<'pending' | 'resolved' | 'dismissed'>('pending');
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [contentDetails, setContentDetails] = useState<any>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const assignedRegions = getAssignedRegions(adminProfile);
    const isRegionScoped = isRegionScopedAdmin(adminProfile);

    const loadReports = useCallback(async () => {
        setLoading(true);
        try {
            const data = await reportService.getReports(filterStatus);
            setReports((data || []).filter((report) =>
                matchesAssignedRegions(
                    adminProfile,
                    report.reported_user?.state,
                    report.reported_user?.location,
                    report.reported_user?.city,
                    report.reporter?.state,
                    report.reporter?.location,
                    report.reporter?.city
                )
            ));
        } catch (error) {
            console.error('Failed to load reports', error);
            alert('Failed to load reports');
        } finally {
            setLoading(false);
        }
    }, [adminProfile, filterStatus]);

    const loadStats = useCallback(async () => {
        try {
            const [pendingReports, resolvedReports, dismissedReports] = await Promise.all([
                reportService.getReports('pending'),
                reportService.getReports('resolved'),
                reportService.getReports('dismissed')
            ]);

            const scopedPending = pendingReports.filter((report) =>
                matchesAssignedRegions(
                    adminProfile,
                    report.reported_user?.state,
                    report.reported_user?.location,
                    report.reported_user?.city,
                    report.reporter?.state,
                    report.reporter?.location,
                    report.reporter?.city
                )
            );
            const scopedResolved = resolvedReports.filter((report) =>
                matchesAssignedRegions(
                    adminProfile,
                    report.reported_user?.state,
                    report.reported_user?.location,
                    report.reported_user?.city,
                    report.reporter?.state,
                    report.reporter?.location,
                    report.reporter?.city
                )
            );
            const scopedDismissed = dismissedReports.filter((report) =>
                matchesAssignedRegions(
                    adminProfile,
                    report.reported_user?.state,
                    report.reported_user?.location,
                    report.reported_user?.city,
                    report.reporter?.state,
                    report.reporter?.location,
                    report.reporter?.city
                )
            );

            setStats({
                pending: scopedPending.length,
                resolved: scopedResolved.length,
                dismissed: scopedDismissed.length,
                highSeverity: scopedPending.filter((report) => ['high', 'critical'].includes(report.severity)).length
            });
        } catch (error) {
            console.error('Failed to load stats', error);
        }
    }, [adminProfile]);

    useEffect(() => {
        loadReports();
        loadStats();
    }, [loadReports, loadStats]);

    const handleViewReport = async (report: Report) => {
        setSelectedReport(report);
        setContentDetails(null);

        if (report.content_type && report.content_id) {
            try {
                const details = await reportService.getContentDetails(report.content_type as any, report.content_id);
                setContentDetails(details);
            } catch (error) {
                console.error('Failed to load content details', error);
            }
        }
    };

    const handleDismiss = async () => {
        if (!selectedReport) return;
        setActionLoading(true);
        try {
            await reportService.updateReportStatus(selectedReport.id, 'dismissed', 'none');
            alert('Report dismissed');
            setSelectedReport(null);
            loadReports();
            loadStats();
        } catch (error) {
            alert('Failed to dismiss report');
        } finally {
            setActionLoading(false);
        }
    };

    const handleHideContent = async () => {
        if (!selectedReport || !selectedReport.content_id) return;

        if (!window.confirm('Are you sure you want to hide this content? It will be invisible to all users.')) {
            return;
        }

        setActionLoading(true);
        try {
            await reportService.hideContent(
                selectedReport.content_type as any,
                selectedReport.content_id,
                `Hidden by admin due to report: ${selectedReport.reason}`
            );

            await reportService.updateReportStatus(selectedReport.id, 'resolved', 'content_removed');

            alert('Content hidden and report resolved');
            setSelectedReport(null);
            loadReports();
            loadStats();
        } catch (error) {
            alert('Failed to hide content');
        } finally {
            setActionLoading(false);
        }
    };

    const getSeverityBadge = (severity: string) => {
        const colors: Record<string, string> = {
            low: 'badge-gray',
            medium: 'badge-blue',
            high: 'badge-yellow',
            critical: 'badge-red'
        };
        return <span className={`badge ${colors[severity] || 'badge-gray'}`}>{severity.toUpperCase()}</span>;
    };

    return (
        <div className="report-management">
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <Flag size={32} />
                        Content Reports
                    </h1>
                    <p className="page-subtitle">Review and moderate reported content</p>
                    {isRegionScoped && (
                        <p className="page-subtitle" style={{ marginTop: '8px', color: '#92400e' }}>
                            Region scope active: {assignedRegions.length > 0 ? assignedRegions.join(', ') : 'No regions assigned'}
                        </p>
                    )}
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                <div className="stat-card stat-warning">
                    <div className="stat-label">Pending Review</div>
                    <div className="stat-value">{stats.pending}</div>
                </div>
                <div className="stat-card stat-danger">
                    <div className="stat-label">High Severity</div>
                    <div className="stat-value">{stats.highSeverity}</div>
                </div>
                <div className="stat-card stat-success">
                    <div className="stat-label">Resolved</div>
                    <div className="stat-value">{stats.resolved}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Dismissed</div>
                    <div className="stat-value">{stats.dismissed}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="filter-group">
                    <Filter size={18} />
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value as any)}
                        className="form-select"
                    >
                        <option value="pending">Pending</option>
                        <option value="resolved">Resolved</option>
                        <option value="dismissed">Dismissed</option>
                    </select>
                </div>
            </div>

            {/* Reports Table */}
            {loading ? (
                <div className="loading-state">Loading reports...</div>
            ) : reports.length === 0 ? (
                <div className="empty-state">
                    <CheckCircle size={48} className="text-green-500" />
                    <h3>All caught up!</h3>
                    <p>No reports found with status: {filterStatus}</p>
                </div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Severity</th>
                                <th>Type</th>
                                <th>Reason</th>
                                <th>Reporter</th>
                                <th>Reported User</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reports.map((report) => (
                                <tr key={report.id}>
                                    <td>{getSeverityBadge(report.severity)}</td>
                                    <td>
                                        {report.content_type === 'post' && <FileText size={16} className="inline mr-1" />}
                                        {report.content_type === 'comment' && <MessageSquare size={16} className="inline mr-1" />}
                                        <span className="capitalize">{report.content_type}</span>
                                    </td>
                                    <td>{report.reason}</td>
                                    <td>
                                        <div className="user-cell">
                                            {report.reporter?.photo_url && (
                                                <img
                                                    src={report.reporter.photo_url}
                                                    alt={report.reporter.display_name || "Reporter"}
                                                    className="user-avatar-sm"
                                                />
                                            )}
                                            <span>{report.reporter?.display_name || 'Unknown'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="user-cell">
                                            {report.reported_user?.photo_url && (
                                                <img
                                                    src={report.reported_user.photo_url}
                                                    alt={report.reported_user.display_name || "Reported user"}
                                                    className="user-avatar-sm"
                                                />
                                            )}
                                            <span>{report.reported_user?.display_name || 'Unknown'}</span>
                                        </div>
                                    </td>
                                    <td>{new Date(report.created_at).toLocaleDateString()}</td>
                                    <td>
                                        <button
                                            className="btn-icon btn-icon-primary"
                                            onClick={() => handleViewReport(report)}
                                            title="View Details"
                                        >
                                            <Eye size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Detail Modal */}
            {selectedReport && (
                <div className="modal-overlay" onClick={() => setSelectedReport(null)}>
                    <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Report Details</h2>
                            <button className="modal-close" onClick={() => setSelectedReport(null)}>&times;</button>
                        </div>

                        <div className="modal-body">
                            <div className="flex gap-4 mb-6">
                                <div className="flex-1 p-4 bg-gray-50 rounded-lg">
                                    <h3 className="font-semibold mb-2">Reporter</h3>
                                    <div className="flex items-center gap-2">
                                        {selectedReport.reporter?.photo_url && (
                                            <img
                                                src={selectedReport.reporter.photo_url}
                                                alt={selectedReport.reporter.display_name || "Reporter"}
                                                className="w-8 h-8 rounded-full"
                                            />
                                        )}
                                        <div>
                                            <div className="font-medium">{selectedReport.reporter?.display_name}</div>
                                            <div className="text-sm text-gray-500">{selectedReport.reporter?.email}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1 p-4 bg-gray-50 rounded-lg">
                                    <h3 className="font-semibold mb-2">Reported User</h3>
                                    <div className="flex items-center gap-2">
                                        {selectedReport.reported_user?.photo_url && (
                                            <img
                                                src={selectedReport.reported_user.photo_url}
                                                alt={selectedReport.reported_user.display_name || "Reported user"}
                                                className="w-8 h-8 rounded-full"
                                            />
                                        )}
                                        <div>
                                            <div className="font-medium">{selectedReport.reported_user?.display_name}</div>
                                            <div className="text-sm text-gray-500">{selectedReport.reported_user?.email}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-6">
                                <h3 className="font-semibold mb-2">Report Info</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><span className="text-gray-500">Reason:</span> {selectedReport.reason}</div>
                                    <div><span className="text-gray-500">Severity:</span> {getSeverityBadge(selectedReport.severity)}</div>
                                    <div className="col-span-2 mt-2">
                                        <span className="text-gray-500">Description:</span>
                                        <ExpandableText text={selectedReport.description || 'No description provided'} limit={150} className="mt-1 p-3 bg-gray-50 rounded border border-gray-200" />
                                    </div>
                                    {selectedReport.additional_details && (
                                        <div className="col-span-2 mt-2">
                                            <span className="text-gray-500">Additional Details (from user):</span>
                                            <div className="mt-1 p-3 bg-blue-50 rounded border border-blue-200 text-gray-800 font-medium italic">
                                                <ExpandableText text={`"${selectedReport.additional_details}"`} limit={150} className="text-gray-800" />
                                            </div>
                                        </div>
                                    )}
                                    <div className="col-span-2 mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                                        <textarea
                                            className="w-full p-2 border rounded-md"
                                            rows={3}
                                            placeholder="Add notes about resolution..."
                                            defaultValue={selectedReport.admin_notes || ''}
                                            onBlur={(e) => reportService.updateAdminNotes(selectedReport.id, e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Content Preview */}
                            {(selectedReport.content_type === 'post' || selectedReport.content_type === 'comment') && (
                                <div className="mb-6 border-t pt-4">
                                    <h3 className="font-semibold mb-2">Content Preview</h3>
                                    {contentDetails ? (
                                        <div className="p-4 border rounded-lg bg-white">
                                            {selectedReport.content_type === 'post' && (
                                                <>
                                                    {contentDetails.media_url && (
                                                        <img src={contentDetails.media_url} className="w-full h-48 object-cover rounded mb-3" alt="Post content" />
                                                    )}
                                                    <p className="text-gray-800">{contentDetails.caption}</p>
                                                </>
                                            )}
                                            {selectedReport.content_type === 'comment' && (
                                                <p className="text-gray-800">{contentDetails.text}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-gray-500 italic">Content not available or deleted</div>
                                    )}
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-8 pt-4 border-t">
                                {selectedReport.status !== 'dismissed' && (
                                    <button
                                        className="btn btn-secondary"
                                        onClick={handleDismiss}
                                        disabled={actionLoading}
                                    >
                                        <CheckCircle size={18} className="mr-2" />
                                        Dismiss Report
                                    </button>
                                )}

                                {selectedReport.status === 'pending' && (selectedReport.content_type === 'post' || selectedReport.content_type === 'comment') && (
                                    <button
                                        className="btn btn-danger"
                                        onClick={handleHideContent}
                                        disabled={actionLoading}
                                    >
                                        <ShieldAlert size={18} className="mr-2" />
                                        Hide Content & Resolve
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportManagement;
